import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";

import { differenceInDays } from "date-fns";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  CurrentUser,
  fetchCurrentUser,
  selectAuthIsError,
  selectAuthIsFetching,
  selectAuthIsSuccess,
  selectCurrentUser,
  selectCurrentUserToken,
  setFeatures,
  setLDIdentified
} from "features/auth/auth-slice";
import { useLazyGetCurrentUserPaywallFeaturesQuery } from "services/paywall";
import { MIGRATION_RECORDS_SOURCES } from "services/types/migration-records.type";
import { parseBackendDateOnly } from "utils/common";
import IntercomSingleton from "utils/intercom.util";
import UserbackSingleton from "utils/userback.util";
import { viteEnv } from "utils/vite-env.util";

// Module-level flags to prevent duplicate fetchCurrentUser calls across all hook instances
let isFetchingUser = false;
let lastFetchedTokenModule: string | null = null;
let fetchFailedForToken: string | null = null;

export default function useAuth() {
  const ldClient = useLDClient();
  const intercom = IntercomSingleton.getInstance();
  const userback = UserbackSingleton.getInstance();
  const [queryPaywallFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();
  const token = useAppSelector(selectCurrentUserToken);
  const user = useAppSelector(selectCurrentUser);
  const isFetching = useAppSelector(selectAuthIsFetching);
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const isError = useAppSelector(selectAuthIsError);

  const dispatch = useAppDispatch();
  const lastFetchedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Only fetch if:
    // 1. We have a token
    // 2. Not already being fetched by another instance (module-level flag)
    // 3. Haven't successfully loaded yet OR token has changed
    // 4. Haven't already failed for this exact token (prevents infinite retry loop)
    if (
      token &&
      !isFetchingUser &&
      (!isSuccess || token !== lastFetchedTokenModule) &&
      fetchFailedForToken !== token
    ) {
      isFetchingUser = true;
      lastFetchedTokenModule = token;
      lastFetchedTokenRef.current = token;

      dispatch(fetchCurrentUser({ token }))
        .unwrap()
        .then(() => {
          fetchFailedForToken = null;
        })
        .catch(() => {
          fetchFailedForToken = token;
        })
        .finally(() => {
          isFetchingUser = false;
        });
    }
  }, [token, isFetching, isSuccess, isError, dispatch]);

  useEffect(() => {
    const loginUser = async (userKey: string, user: CurrentUser) => {
      try {
        await ldClient?.identify({
          kind: "user",
          key: userKey,
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          avatar: user.photo,
          firstName: user.first_name,
          lastName: user.last_name,
          custom: {
            practice: user.practice?.name,
            practiceId: user.practice?.id,
            roles: user.groups.map((group) => group.name)
          }
        });
        dispatch(setLDIdentified(true));
      } catch (e) {
        console.log("ld identify failure:", e);
        dispatch(setLDIdentified(false));
      }
    };
    if (user.id !== -1) {
      const name = `${user.first_name.trim()} ${user.last_name.trim()}`.trim();
      const userKey = user.id;

      queryPaywallFeatures().then((resp) => {
        if ("data" in resp) {
          dispatch(setFeatures(resp.data));
        } else {
          console.log("Error getting paywall features:", resp.error);
        }
      });
      loginUser(userKey.toString(), user);
      intercom.identify({
        email: user.email,
        user_id: user.id.toString(),
        name: name.trim(),
        created_at: new Date(user.date_joined!).getTime() / 1000,
        avatar: {
          type: "avatar",
          image_url: user.photo
        },
        user_role: user.role ?? "",
        user_groups: user.groups.map((g) => g.name).join(", "),
        suno_env: viteEnv.VITE_ENV || "local",
        company: user.practice
          ? {
              company_id: user.practice.id.toString(),
              name: user.practice!.name || "",
              previous_oms: user.practice.previous_oms
                ? MIGRATION_RECORDS_SOURCES.getFromCode(
                    user.practice.previous_oms
                  )
                : "",
              go_live_date: user.practice?.go_live_at
                ? parseBackendDateOnly(user.practice.go_live_at).getTime() /
                  1000
                : undefined,
              new_customer: user.practice?.go_live_at
                ? differenceInDays(
                    new Date(),
                    parseBackendDateOnly(user.practice.go_live_at)
                  ) < 14 // go live date is less than 2 weeks ago
                : false
            }
          : undefined
      });

      // Initialize Userback with user data
      userback.identify({
        id: user.id.toString(),
        name: name.trim(),
        email: user.email
      });
    }
  }, [user, ldClient, dispatch, intercom, userback, queryPaywallFeatures]);
  return null;
}
